/**
 * Chat hooks — V5-01 §5.
 *
 * Thin over the REST contract in `server/src/modules/chat/README.md`. Realtime
 * arrives through the shared socket and invalidates these queries rather than
 * patching them by hand: a thread is small and re-fetching it is cheaper than
 * getting the merge subtly wrong.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ChatPerson {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  designation?: string;
}

export interface ConversationView {
  _id: string;
  type: 'dm' | 'group' | 'company' | 'channel';
  participants: { _id: string; name: string; avatarUrl?: string }[];
  name?: string;
  emoji?: string;
  colour?: string;
  adminIds: string[];
  isAdmin: boolean;
  participantCount: number;
  lastMessageAt?: string;
  lastMessagePreview?: { senderId?: string; kind: string; text: string };
  unreadCount: number;
  /** §8 — how much of the unread was addressed to me; what a channel row shows. */
  unreadMentionCount?: number;
  muted: boolean;
  /* ── V7-04 ─────────────────────────────────────────── */
  visibility?: 'public' | 'private';
  postingMode?: 'everyone' | 'admins';
  isArchived?: boolean;
  projectId?: string | null;
  /** §8 — channels default to `mentions`, everything else to `all`. */
  notifyLevel?: 'all' | 'mentions' | 'mute';
  /** Whether this person may post right now — archived and announcements. */
  canPost?: boolean;
  pinnedCount?: number;
}

export interface GroupMemberView {
  _id: string;
  name: string;
  avatarUrl?: string | null;
  designation?: string | null;
  isAdmin: boolean;
}

export interface MessageInfoRow {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  deliveredAt: string | null;
  readAt: string | null;
}

export interface ChatAttachmentView {
  _id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  isImage: boolean;
  /**
   * V10 §9 — signed, inline image URLs from the server.
   *
   * Before this the client built `/api/v1/attachments/:id/thumbnail`, a route
   * that has never existed, so every chat image was an `<img>` on a 404.
   */
  thumbUrl?: string | null;
  previewUrl?: string | null;
}

export interface ChatMessageView {
  _id: string;
  conversationId: string;
  senderId?: string;
  kind: 'user' | 'system';
  body: string;
  attachments: ChatAttachmentView[];
  taskRef?: {
    taskId: string;
    projectKey: string;
    taskKey: string;
    titleSnapshot: string;
    event: string;
    /** V6-05 §6 — the chip is live, so it carries current state. */
    statusName?: string;
    state?: 'open' | 'done' | 'reassigned' | 'deleted';
  };
  replyTo?: {
    messageId: string;
    senderId?: string;
    senderName?: string;
    snippet: string;
  };
  mentionIds?: string[];
  groupEvent?: string;
  /* ── V7-04 ─────────────────────────────────────────── */
  /** §2 — set on a reply. A message with this is never in the main scroll. */
  threadRootId?: string;
  /** §2 — denormalised onto the root, so "3 replies" costs no query. */
  replyCount?: number;
  lastReplyAt?: string;
  /** §3 — aggregated with the viewer's own membership resolved server-side. */
  reactions?: { emoji: string; count: number; mine: boolean; names: string[] }[];
  /** §5 */
  pinnedAt?: string;
  saved?: boolean;
  /** §4 — title-only, no images. */
  unfurls?: { url: string; title: string; siteName?: string }[];
  /** §4 — derived server-side, and only ever present on your own messages. */
  tick?: 'sent' | 'delivered' | 'read' | null;
  /** V8-02 §2 — when the weakest link reached each state, for the tooltip. */
  deliveredAt?: string | null;
  readAt?: string | null;
  createdAt: string;
  editedAt?: string;
  deletedAt?: string;
}

export function useChatPeople(query: string) {
  return useQuery({
    queryKey: ['chat-people', query],
    queryFn: async () =>
      (await api.get<ChatPerson[]>(`/chat/people?q=${encodeURIComponent(query)}`)).data,
  });
}

export function useConversations() {
  return useQuery({
    queryKey: ['chat-conversations'],
    queryFn: async () => (await api.get<ConversationView[]>('/chat/conversations')).data,
  });
}

/** The sidebar badge and the document title. */
export function useChatUnread(enabled = true) {
  return useQuery({
    queryKey: ['chat-unread'],
    enabled,
    queryFn: async () => (await api.get<{ total: number }>('/chat/unread')).data,
  });
}

export function useMessages(conversationId: string | undefined, before?: string) {
  return useQuery({
    queryKey: ['chat-messages', conversationId, before ?? 'latest'],
    enabled: Boolean(conversationId),
    queryFn: async () =>
      (
        await api.get<ChatMessageView[]>(
          `/chat/conversations/${conversationId}/messages${before ? `?before=${encodeURIComponent(before)}` : ''}`,
        )
      ).data,
  });
}

/**
 * Upload a file for the composer — V5-01 §5.
 *
 * The same presign flow tasks and notes use, with `entityType: 'chat_message'`
 * and the *conversation* as the entity: the file exists before the message
 * does, so the conversation is what owns it and what membership is checked
 * against. Bytes go straight to storage; the API only ever sees metadata, and
 * they count against the workspace's V5-07 storage quota like any other file.
 */
export async function uploadChatFile(
  conversationId: string,
  file: File,
): Promise<ChatAttachmentView> {
  const { data: presigned } = await api.post<{ attachmentId: string; uploadUrl: string }>(
    '/attachments/presign',
    {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      entityType: 'chat_message',
      entityId: conversationId,
    },
  );

  const put = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type || 'application/octet-stream' },
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);

  const { data: completed } = await api.post<{
    _id: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
  }>(`/attachments/${presigned.attachmentId}/complete`, {});

  return {
    _id: completed._id,
    fileName: completed.fileName,
    mimeType: completed.mimeType,
    sizeBytes: completed.sizeBytes,
    isImage: (completed.mimeType ?? '').startsWith('image/'),
  };
}

/** One page older than a given timestamp — §5 infinite scroll. */
export async function fetchOlderMessages(
  conversationId: string,
  before: string,
): Promise<ChatMessageView[]> {
  return (
    await api.get<ChatMessageView[]>(
      `/chat/conversations/${conversationId}/messages?before=${encodeURIComponent(before)}`,
    )
  ).data;
}

/** A short-lived signed GET, resolved at click time — same as §F8 elsewhere. */
/**
 * The signed download link for a chat attachment — V10 §5, applied here too.
 *
 * This used to `fetch` the 302 endpoint, which follows the redirect and pulls
 * the entire file into memory only to read `res.url` off it and throw the bytes
 * away. The JSON sibling is one round trip and carries the file name, which is
 * what an anchor needs.
 */
export async function chatDownloadLink(
  attachmentId: string,
): Promise<{ url: string; fileName: string }> {
  return (
    await api.get<{ url: string; fileName: string }>(
      `/attachments/${attachmentId}/download-link`,
    )
  ).data;
}

function useChatMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
      void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      void qc.invalidateQueries({ queryKey: ['chat-unread'] });
    },
  });
}

export function useStartConversation() {
  return useChatMutation(
    async (userId: string) =>
      (await api.post<ConversationView>('/chat/conversations', { userId })).data,
  );
}

export function useSendMessage() {
  return useChatMutation(
    async ({
      conversationId,
      body,
      attachments,
      replyTo,
    }: {
      conversationId: string;
      body: string;
      attachments?: string[];
      replyTo?: string;
    }) =>
      (
        await api.post<ChatMessageView>(`/chat/conversations/${conversationId}/messages`, {
          body,
          attachments,
          replyTo,
        })
      ).data,
  );
}

/* ── Groups (§1) ───────────────────────────────────────── */

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; userIds: string[]; emoji?: string }) =>
      (await api.post<ConversationView>('/chat/groups', input)).data,
    onSuccess: (conversation) => {
      /**
       * V8-02 §4 — put the new group in the list *before* the refetch lands.
       *
       * The header renders from the conversation *list*, not from this
       * response, so between creating a group and the list coming back there
       * was no row to read and the header fell back to a bare "New
       * conversation" bar with no participants button on it. On this machine
       * that gap is under a second; on the founder's connection it is however
       * long his round trip takes, and what he does in it is click a control
       * that is not there yet and report that he cannot add anybody.
       *
       * Seeding is safe because the response *is* the authoritative row — the
       * same shape the list returns — and the invalidation below still
       * reconciles it a moment later.
       */
      qc.setQueryData<ConversationView[]>(['chat-conversations'], (current) =>
        current && !current.some((row) => row._id === conversation._id)
          ? [conversation, ...current]
          : current,
      );
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
      void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      void qc.invalidateQueries({ queryKey: ['chat-unread'] });
    },
  });
}

export function useGroupMembers(conversationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['chat-members', conversationId],
    enabled: Boolean(conversationId) && enabled,
    queryFn: async () =>
      (await api.get<GroupMemberView[]>(`/chat/conversations/${conversationId}/members`)).data,
  });
}

function useMembershipMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      void qc.invalidateQueries({ queryKey: ['chat-members'] });
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
      void qc.invalidateQueries({ queryKey: ['chat-unread'] });
    },
  });
}

export function useRenameConversation() {
  return useMembershipMutation(
    async ({ id, name }: { id: string; name: string }) =>
      (await api.patch<ConversationView>(`/chat/conversations/${id}/name`, { name })).data,
  );
}

export function useAddMembers() {
  return useMembershipMutation(
    async ({ id, userIds }: { id: string; userIds: string[] }) =>
      (await api.post(`/chat/conversations/${id}/members`, { userIds })).data,
  );
}

export function useRemoveMember() {
  return useMembershipMutation(
    async ({ id, userId }: { id: string; userId: string }) =>
      (await api.delete(`/chat/conversations/${id}/members`, { userId })).data,
  );
}

export function usePromoteMember() {
  return useMembershipMutation(
    async ({ id, userId }: { id: string; userId: string }) =>
      (await api.post(`/chat/conversations/${id}/admins`, { userId })).data,
  );
}

export function useLeaveConversation() {
  return useMembershipMutation(
    async (id: string) => (await api.post(`/chat/conversations/${id}/leave`, {})).data,
  );
}

export function useDeleteConversation() {
  return useMembershipMutation(
    async ({ id, confirmName }: { id: string; confirmName: string }) =>
      (await api.delete(`/chat/conversations/${id}`, { confirmName })).data,
  );
}

/** §3 — "Delete chat", this side only. */
export function useClearConversation() {
  return useMembershipMutation(
    async (id: string) => (await api.post(`/chat/conversations/${id}/clear`, {})).data,
  );
}

/**
 * The V5-01 mute switch.
 *
 * No longer called by this client — V7-04 §8 replaced the header toggle with
 * three levels, and `setNotifyLevel` writes `muted` in step so nothing
 * downstream had to change. Kept because `POST /conversations/:id/mute` is
 * still part of the documented REST contract the chat README describes for a
 * future standalone messenger, and deleting the binding here would not delete
 * the endpoint — it would only make it undiscoverable from the code.
 */
export function useSetMuted() {
  return useMembershipMutation(
    async ({ id, muted }: { id: string; muted: boolean }) =>
      (await api.post(`/chat/conversations/${id}/mute`, { muted })).data,
  );
}

/* ── Receipts (§4) ─────────────────────────────────────── */

export function useMessageInfo(messageId: string | null) {
  return useQuery({
    queryKey: ['chat-message-info', messageId],
    enabled: Boolean(messageId),
    queryFn: async () =>
      (
        await api.get<{ tick: string | null; rows: MessageInfoRow[] }>(
          `/chat/messages/${messageId}/info`,
        )
      ).data,
  });
}

/**
 * "I have it" — fire-and-forget, like the typing ping.
 *
 * A receipt that fails is not worth telling anyone about: the next read marks
 * the same position, and an error toast about an acknowledgement would be
 * noise about something the person never asked for.
 */
export async function pingDelivered(conversationId: string): Promise<void> {
  await api.post(`/chat/conversations/${conversationId}/delivered`, {}).catch(() => undefined);
}

export function useEditMessage() {
  return useChatMutation(
    async ({ id, body }: { id: string; body: string }) =>
      (await api.patch<ChatMessageView>(`/chat/messages/${id}`, { body })).data,
  );
}

export function useDeleteMessage() {
  return useChatMutation(
    async (id: string) => (await api.delete<ChatMessageView>(`/chat/messages/${id}`)).data,
  );
}

export function useMarkRead() {
  return useChatMutation(
    async (conversationId: string) =>
      (await api.post(`/chat/conversations/${conversationId}/read`, {})).data,
  );
}

/** Fire-and-forget: a typing ping that fails is not worth telling anyone about. */
export async function pingTyping(conversationId: string): Promise<void> {
  await api.post(`/chat/conversations/${conversationId}/typing`, {}).catch(() => undefined);
}

/* ══ V7-04 ══════════════════════════════════════════════ */

/* ── §1 Channels ───────────────────────────────────────── */

export interface BrowsableChannel {
  _id: string;
  name: string;
  emoji?: string;
  colour?: string;
  visibility: string;
  memberCount: number;
  isMember: boolean;
  isArchived: boolean;
  projectId: string | null;
  lastMessageAt: string | null;
}

export function useBrowseChannels(query: string, enabled = true) {
  return useQuery({
    queryKey: ['chat-channels-browse', query],
    queryFn: async () =>
      (await api.get<BrowsableChannel[]>(`/chat/channels/browse?q=${encodeURIComponent(query)}`))
        .data,
    enabled,
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      visibility: 'public' | 'private';
      userIds?: string[];
    }) => (await api.post<ConversationView>('/chat/channels', input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      void qc.invalidateQueries({ queryKey: ['chat-channels-browse'] });
    },
  });
}

export function useJoinChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<ConversationView>(`/chat/channels/${id}/join`, {})).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-conversations'] });
      void qc.invalidateQueries({ queryKey: ['chat-channels-browse'] });
    },
  });
}

/**
 * §1 — a project's channel, found or created.
 *
 * Idempotent on the server, so the button is safe to press twice and safe to
 * press on a project that already has one: the second call opens the first
 * channel rather than making a second.
 */
export function useProjectChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) =>
      (await api.post<ConversationView>(`/chat/projects/${projectId}/channel`, {})).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-conversations'] }),
  });
}

export function useSetArchived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; archived: boolean }) =>
      (
        await api.post<ConversationView>(`/chat/conversations/${input.id}/archive`, {
          archived: input.archived,
        })
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-conversations'] }),
  });
}

export function useSetPostingMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; mode: 'everyone' | 'admins' }) =>
      (
        await api.post<ConversationView>(`/chat/conversations/${input.id}/posting-mode`, {
          mode: input.mode,
        })
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-conversations'] }),
  });
}

/* ── §8 Notification level ─────────────────────────────── */

export function useSetNotifyLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; level: 'all' | 'mentions' | 'mute' }) =>
      (
        await api.post<{ notifyLevel: string }>(`/chat/conversations/${input.id}/notify-level`, {
          level: input.level,
        })
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-conversations'] }),
  });
}

/* ── §2 Threads ────────────────────────────────────────── */

export function useThread(rootId: string | null) {
  return useQuery({
    queryKey: ['chat-thread', rootId],
    queryFn: async () =>
      (await api.get<ChatMessageView[]>(`/chat/messages/${rootId}/thread`)).data,
    enabled: Boolean(rootId),
  });
}

export function useReplyInThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { rootId: string; body: string; attachments?: string[] }) =>
      (
        await api.post<ChatMessageView>(`/chat/messages/${input.rootId}/thread`, {
          body: input.body,
          attachments: input.attachments,
        })
      ).data,
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: ['chat-thread', input.rootId] });
      // The parent's "3 replies" counter lives on the main scroll.
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
      void qc.invalidateQueries({ queryKey: ['chat-threads'] });
    },
  });
}

export function useFollowThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { rootId: string; follow: boolean }) =>
      (
        await api.post<{ following: boolean }>(`/chat/messages/${input.rootId}/follow`, {
          following: input.follow,
        })
      ).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-threads'] }),
  });
}

export interface FollowedThread {
  root: ChatMessageView;
  conversation: { _id: string; name?: string; type: string };
  replyCount: number;
  lastReplyAt: string | null;
  /** §2 — moved since I last read its conversation. Badges the Threads tab. */
  hasNewReplies: boolean;
}

export function useFollowedThreads(enabled = true) {
  return useQuery({
    queryKey: ['chat-threads'],
    queryFn: async () => (await api.get<FollowedThread[]>('/chat/threads')).data,
    enabled,
  });
}

/* ── §3 Reactions ──────────────────────────────────────── */

export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; emoji: string }) =>
      (
        await api.post<{ reactions: { emoji: string; userIds: string[]; mine: boolean }[] }>(
          `/chat/messages/${input.messageId}/reactions`,
          { emoji: input.emoji },
        )
      ).data,
    // No optimistic update: the socket's `chat:message:edited` carries the
    // authoritative aggregate, and two people reacting at once would otherwise
    // each see their own count win briefly.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-messages'] }),
  });
}

/* ── §5 Saved & pins ───────────────────────────────────── */

export function useToggleSaved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; saved: boolean }) =>
      (
        await api.post<{ saved: boolean }>(`/chat/messages/${input.messageId}/save`, {
          saved: input.saved,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-saved'] });
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    },
  });
}

export interface SavedMessageView {
  message: ChatMessageView;
  conversation: { _id: string; name?: string; type: string };
}

export function useSavedMessages(enabled = true) {
  return useQuery({
    queryKey: ['chat-saved'],
    queryFn: async () => (await api.get<SavedMessageView[]>('/chat/saved')).data,
    enabled,
  });
}

export function useTogglePin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; pinned: boolean }) =>
      (
        await api.post<ChatMessageView>(`/chat/messages/${input.messageId}/pin`, {
          pinned: input.pinned,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['chat-pins'] });
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    },
  });
}

export function usePins(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['chat-pins', conversationId],
    queryFn: async () =>
      (await api.get<ChatMessageView[]>(`/chat/conversations/${conversationId}/pins`)).data,
    enabled: Boolean(conversationId),
  });
}

/* ── §4 Message → task ─────────────────────────────────── */

/**
 * The prefill the modal opens with — §4. Read-only; nothing is created.
 */
export function useTaskDraft(messageId: string | null) {
  return useQuery({
    queryKey: ['chat-task-draft', messageId],
    queryFn: async () =>
      (
        await api.get<{
          title: string;
          description: Record<string, unknown>;
          suggestedAssigneeId: string | null;
          sourceMessageId: string;
          conversationId: string;
        }>(`/chat/messages/${messageId}/task-draft`)
      ).data,
    enabled: Boolean(messageId),
    // A draft is a snapshot of a message at the moment somebody clicked; it
    // must not refetch under the open modal and change what they are editing.
    staleTime: Infinity,
  });
}

/**
 * The second half of the round trip — §4.
 *
 * The task itself is created through the ordinary `POST /tasks`, deliberately:
 * a second creation path would be a second place for project validation,
 * permissions, automations and activity to be got wrong. This posts the chip
 * once that has succeeded, and is idempotent, so a retried create leaves one.
 */
export function usePostTaskChip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { messageId: string; taskId: string }) =>
      (
        await api.post<ChatMessageView>(`/chat/messages/${input.messageId}/task-chip`, {
          taskId: input.taskId,
        })
      ).data,
    onSuccess: (_data, input) => {
      // The chip lands as a thread reply under the source message.
      void qc.invalidateQueries({ queryKey: ['chat-thread', input.messageId] });
      void qc.invalidateQueries({ queryKey: ['chat-messages'] });
    },
  });
}

/* ── §6 Search ─────────────────────────────────────────── */

export interface ChatSearchHit {
  message: ChatMessageView;
  conversation: { _id: string; name?: string; type: string };
  /** §2 — a hit inside a thread opens its thread, not the scroll. */
  threadRootId: string | null;
}

export function useChatSearch(
  query: string,
  options: { conversationId?: string; from?: string; hasFile?: boolean } = {},
) {
  const params = new URLSearchParams({ q: query.trim() });
  if (options.conversationId) params.set('conversationId', options.conversationId);
  if (options.from) params.set('from', options.from);
  if (options.hasFile) params.set('hasFile', 'true');

  return useQuery({
    queryKey: ['chat-search', params.toString()],
    queryFn: async () => (await api.get<ChatSearchHit[]>(`/chat/search?${params}`)).data,
    // One character is not a search, it is a scan of the whole workspace.
    enabled: query.trim().length >= 2,
  });
}
